import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Users, Shield, Trash2, CheckCircle, XCircle, Search,
  RefreshCw, Mail, Phone, MapPin, Building2, KeyRound, Eye,
  Download, Upload, ArrowRightLeft
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const AdminUsersPage = () => {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferSource, setTransferSource] = useState("");
  const [transferTarget, setTransferTarget] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [importTargetUser, setImportTargetUser] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersRes, statsRes] = await Promise.all([
        api.get("/api/admin/users"),
        api.get("/api/admin/stats")
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Sie haben keine Berechtigung für diese Seite. Nur Super-Admin hat Zugriff.");
      } else {
        setError("Fehler beim Laden der Daten");
      }
      toast.error(err.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDeleteUser = async (userId, email) => {
    try {
      await api.delete(`/api/admin/users/${userId}`);
      toast.success(`Benutzer ${email} wurde gelöscht`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleVerifyUser = async (userId) => {
    try {
      await api.put(`/api/admin/users/${userId}/verify`);
      toast.success("E-Mail wurde manuell verifiziert");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler bei der Verifizierung");
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/api/admin/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vywork-backup-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup erstellt (${res.data.counts?.customers || 0} Kunden, ${res.data.counts?.work_orders || 0} Aufträge)`);
    } catch (err) {
      toast.error("Fehler beim Export");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      // If a target user is selected, include it in the request
      if (importTargetUser) {
        data.target_user_id = importTargetUser;
      }
      const res = await api.post("/api/admin/import", data);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Import");
    }
    e.target.value = "";
  };

  const handleTransferData = async () => {
    if (!transferSource || !transferTarget) {
      toast.error("Bitte Quell- und Ziel-Benutzer auswählen");
      return;
    }
    if (transferSource === transferTarget) {
      toast.error("Quell- und Ziel-Benutzer müssen unterschiedlich sein");
      return;
    }
    setTransferring(true);
    try {
      const res = await api.post("/api/admin/transfer-data", {
        source_user_id: transferSource,
        target_user_id: transferTarget
      });
      toast.success(res.data.message);
      setTransferOpen(false);
      setTransferSource("");
      setTransferTarget("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Übertragen");
    } finally {
      setTransferring(false);
    }
  };

  const handleResetPassword = async (userId, email) => {
    try {
      await api.post(`/api/admin/users/${userId}/reset-password`);
      toast.success(`Passwort-Reset-E-Mail wurde an ${email} gesendet`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Senden der E-Mail");
    }
  };

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.ansprechpartner || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.firmenname || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-red-700">
              <Shield className="w-6 h-6" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            Admin-Verwaltung
          </h1>
          <p className="text-muted-foreground mt-1">
            Benutzerkonten verwalten
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={handleExport} variant="outline" className="gap-2" data-testid="export-backup-btn">
            <Download className="w-4 h-4" />
            Backup
          </Button>

          {/* Import with target user selection */}
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="import-backup-btn">
                <Upload className="w-4 h-4" />
                Importieren
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Backup importieren</DialogTitle>
                <DialogDescription>
                  Wählen Sie optional einen Ziel-Benutzer, dem die Daten zugeordnet werden sollen.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Ziel-Benutzer (optional)</Label>
                  <Select value={importTargetUser} onValueChange={setImportTargetUser}>
                    <SelectTrigger data-testid="import-target-select">
                      <SelectValue placeholder="Standard: Aktueller Admin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Aktueller Admin</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name || u.ansprechpartner || u.email} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label>
                  <Button className="gap-2 cursor-pointer w-full" asChild>
                    <span>
                      <Upload className="w-4 h-4" />
                      JSON-Datei auswählen und importieren
                    </span>
                  </Button>
                  <input type="file" accept=".json" className="hidden" onChange={handleImport} />
                </label>
              </div>
            </DialogContent>
          </Dialog>

          {/* Transfer Data between users */}
          <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="transfer-data-btn">
                <ArrowRightLeft className="w-4 h-4" />
                Daten übertragen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Daten zwischen Benutzern übertragen</DialogTitle>
                <DialogDescription>
                  Alle Kunden, Fahrzeuge, Aufträge und Einstellungen vom Quell-Benutzer werden dem Ziel-Benutzer zugeordnet.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Von (Quelle)</Label>
                  <Select value={transferSource} onValueChange={setTransferSource}>
                    <SelectTrigger data-testid="transfer-source-select">
                      <SelectValue placeholder="Quell-Benutzer wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name || u.ansprechpartner || u.email} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nach (Ziel)</Label>
                  <Select value={transferTarget} onValueChange={setTransferTarget}>
                    <SelectTrigger data-testid="transfer-target-select">
                      <SelectValue placeholder="Ziel-Benutzer wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name || u.ansprechpartner || u.email} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={handleTransferData} 
                  disabled={transferring || !transferSource || !transferTarget}
                  className="w-full gap-2"
                  data-testid="transfer-confirm-btn"
                >
                  {transferring ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <ArrowRightLeft className="w-4 h-4" />
                  )}
                  Daten übertragen
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.total_users}</p>
                  <p className="text-xs text-muted-foreground">Benutzer gesamt</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.verified_users}</p>
                  <p className="text-xs text-muted-foreground">Verifiziert</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.unverified_users}</p>
                  <p className="text-xs text-muted-foreground">Nicht verifiziert</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Registrierte Benutzer</CardTitle>
          <CardDescription>
            Alle Benutzerkonten im System
          </CardDescription>
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Suchen nach E-Mail, Name oder Firma..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Laden...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ansprechpartner / Firma</TableHead>
                    <TableHead>E-Mail</TableHead>
                    <TableHead>Adresse</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{user.ansprechpartner || "-"}</p>
                          {user.firmenname && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {user.firmenname}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="flex items-center gap-1">
                            <Mail className="w-3 h-3 text-muted-foreground" />
                            {user.email}
                          </p>
                          {user.telefon && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {user.telefon}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.strasse ? (
                          <div className="text-sm">
                            <p>{user.strasse} {user.hausnummer}</p>
                            <p className="text-muted-foreground">{user.plz} {user.stadt}</p>
                            {user.land && user.land !== "Deutschland" && (
                              <p className="text-muted-foreground">{user.land}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.email_verified ? (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Verifiziert
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-orange-600 border-orange-300">
                            <XCircle className="w-3 h-3 mr-1" />
                            Nicht verifiziert
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(user.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {/* View Details */}
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedUser(user)}
                                title="Details anzeigen"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-md">
                              <DialogHeader>
                                <DialogTitle>Benutzer-Details</DialogTitle>
                                <DialogDescription>
                                  Vollständige Informationen zum Benutzerkonto
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-muted-foreground">Ansprechpartner</p>
                                    <p className="font-medium">{user.ansprechpartner || "-"}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Firma</p>
                                    <p className="font-medium">{user.firmenname || "-"}</p>
                                  </div>
                                  <div className="col-span-2">
                                    <p className="text-muted-foreground">E-Mail</p>
                                    <p className="font-medium">{user.email}</p>
                                  </div>
                                  <div className="col-span-2">
                                    <p className="text-muted-foreground">Telefon</p>
                                    <p className="font-medium">{user.telefon || "-"}</p>
                                  </div>
                                  <div className="col-span-2">
                                    <p className="text-muted-foreground">Adresse</p>
                                    <p className="font-medium">
                                      {user.strasse ? (
                                        <>
                                          {user.strasse} {user.hausnummer}<br />
                                          {user.plz} {user.stadt}<br />
                                          {user.land}
                                        </>
                                      ) : "-"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Status</p>
                                    <p className="font-medium">
                                      {user.email_verified ? "✅ Verifiziert" : "⏳ Nicht verifiziert"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Erstellt</p>
                                    <p className="font-medium">{formatDate(user.created_at)}</p>
                                  </div>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>

                          {/* Verify Email */}
                          {!user.email_verified && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleVerifyUser(user.id)}
                              title="Manuell verifizieren"
                            >
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            </Button>
                          )}

                          {/* Reset Password */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Passwort zurücksetzen"
                              >
                                <KeyRound className="w-4 h-4 text-blue-600" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Passwort zurücksetzen?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Eine E-Mail mit einem Link zum Zurücksetzen des Passworts wird an 
                                  <strong> {user.email}</strong> gesendet.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleResetPassword(user.id, user.email)}
                                >
                                  E-Mail senden
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>

                          {/* Delete User */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Benutzer löschen"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Möchten Sie den Benutzer <strong>{user.email}</strong> wirklich löschen?
                                  <br /><br />
                                  <strong className="text-red-600">
                                    Alle Daten dieses Benutzers werden unwiderruflich gelöscht!
                                  </strong>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-red-600 hover:bg-red-700"
                                  onClick={() => handleDeleteUser(user.id, user.email)}
                                >
                                  Endgültig löschen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Keine Benutzer gefunden
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUsersPage;
